import { Pipe, PipeTransform, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable ,  of ,  forkJoin } from 'rxjs';
import { map ,  mergeMap } from 'rxjs/operators';
import { TranslationBasePipe } from './translation-base-pipe';

/**
 * @ngModule SharedModule
 * @whatItDoes Formats a list using locale rules.
 * @howToUse `array_expression | translateList[:getter[:translateValues]]`
 * @description
 *
 * Use `translateList` to format a list using locale rules.
 * This defaults to the current locale. In US-English, this means commas between items.
 *
 * - `getter` A property name or map function to use to retrieve values from the items in the list.
 * - `translateValues` Set to true to perform translation of the value of each item in the list as well.
 */
@Pipe({
  name: 'translateList'
})
export class TranslateListPipe extends TranslationBasePipe implements PipeTransform {
  count = 0;

  constructor(translate: TranslateService, changeDetector: ChangeDetectorRef) {
    super(translate, changeDetector);
  }

  private static _createItemTransformer(getter: any): (value?: any, index?: number, array?: any[]) => string {
    if (!getter || getter === '') {
      return value => value;
    } else if (typeof getter === 'string') {
      return value => value[getter];
    } else if (typeof getter === 'function') {
      return getter;
    }

    throw new Error('Invalid property getter: expected a string or a function.');
  }

  protected compareArgs(oldValue: any, oldArgs: any[], newValue: any, newArgs: any[]) {
    if (oldValue instanceof Array && newValue instanceof Array) {
      if (oldValue.length !== newValue.length) {
        return false;
      }

      for (let i = 0; i < oldValue.length; i++) {
        if (oldValue[i] !== newValue[i]) {
          return false;
        }
      }

      oldValue = newValue = true;
    }

    return super.compareArgs(oldValue, oldArgs, newValue, newArgs);
  }

  protected performTranslation(list: any, args: any[]): Observable<string> {
    const getter = args ? args[0] : null;
    const translateValues = args && args.length > 0 ? args[1] : null;

    const callback = TranslateListPipe._createItemTransformer(getter);
    // Transform the item list into a list of strings.
    const items = list.map(callback);

    if (!items.length) {
      return of('');
    }

    // Transform the list of strings into a list of observables.
    // If we are flagged to treat the strings as translation keys, have those observables
    // perform that translation; otherwise, just have them return the raw strings.
    let observables: Observable<string>[];
    if (translateValues) {
      observables = items.map(item => this.translateService.get(item));
    } else {
      observables = items.map(item => of(item));
    }

    // Map each item text (either raw or translated now) as a parameter for a localizable string
    // that formats that item for its position within the list.
    if (items.length === 1) {
      return this._mergeListTranslation(observables[0], 'COMMON.DELIMITED_LIST_ONLY_ITEM');
    }

    observables[0] = this._mergeListTranslation(observables[0], 'COMMON.DELIMITED_LIST_FIRST_ITEM');
    for (let i = 1; i < observables.length - 1; i++) {
      observables[i] = this._mergeListTranslation(observables[i], 'COMMON.DELIMITED_LIST_MIDDLE_ITEM');
    }
    observables[observables.length - 1] =
    this._mergeListTranslation(observables[observables.length - 1], 'COMMON.DELIMITED_LIST_LAST_ITEM');

    // Take all the list-formatted items and concatenate them together into a single string.
    return forkJoin(observables).pipe(map(values => values.join('')));
  }

  private _mergeListTranslation(observableItem: Observable<string>, key: string) {
    return observableItem.pipe(mergeMap(item => this.translateService.get(key, { item })));
  }
}
